import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useGlobal } from '../global/GlobalContext';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import compressImage from '../utils/compressImage';
import { cacheManager } from '../utils/cacheManager';

function PlantersCategory({ title }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useGlobal();

  const fetchPlantersCategory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const cached = cacheManager.get('planters');
      if (cached) {
        setCategories(cached);
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/get_slug_data/planters`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 && response.data?.category_data?.child_categories) {
        const fetchedCategories = response.data.category_data.child_categories;
        const isArray = Array.isArray(fetchedCategories)
          ? fetchedCategories
          : [fetchedCategories];

        // The API has occasionally returned the same category twice (seen
        // in production data) — de-dupe by id/slug so it never renders twice.
        const seen = new Set();
        const deduped = isArray.filter((cat) => {
          const key = cat?.id ?? cat?.slug;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setCategories(deduped);
        cacheManager.set('planters', deduped);
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (err) {
      setError('Error fetching categories. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPlantersCategory();
  }, [fetchPlantersCategory]);

  return (
    <div id="shop-by-category" className="pt-3 max-w-7xl mx-auto px-4 scroll-mt-24">
      <div className="flex items-center justify-center gap-4 mb-8">
        <span className="h-px w-10 md:w-16 bg-brand-500" />
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 capitalize whitespace-nowrap">
          {title}
        </h2>
        <span className="h-px w-10 md:w-16 bg-brand-500" />
      </div>
      {error && <p className="text-red-500 text-center">{error}</p>}
      {loading ? (
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-6 md:gap-x-10">
          {Array.from({ length: 7 }).map((_, index) => (
            <div className="flex flex-col items-center gap-3" key={index}>
              <Skeleton circle height={96} width={96} />
              <Skeleton height={12} width={70} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Desktop/tablet — horizontal carousel.
              Wrapped in a plain div for the hidden/md:block toggle rather than
              applying it to the Swiper root directly — Swiper's own stylesheet
              (swiper/css) sets `.swiper { display: block }`, which can beat
              Tailwind's `hidden` utility on the same element via CSS source
              order (both are single-class selectors), leaving it visible on
              mobile regardless of the "hidden" class actually being present. */}
          <div className="hidden md:block">
            <Swiper
              navigation
              modules={[Navigation]}
              spaceBetween={16}
              breakpoints={{
                0: { slidesPerView: 3.2 },
                480: { slidesPerView: 4.2 },
                768: { slidesPerView: 5.2 },
                1024: { slidesPerView: 7 },
              }}
              className="category_swiper !pb-2"
            >
              {Array.isArray(categories) && categories.map((cat) => (
                <SwiperSlide key={cat.id}>
                  <Link
                    to={`/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 text-center"
                  >
                    <div className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-full bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center group-hover:border-brand-600/40 group-hover:shadow-md transition-all">
                      <img
                        src={compressImage(cat.cat_img, 300, 70, 'webp')}
                        alt={cat.category_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-xs md:text-sm font-semibold text-gray-700 group-hover:text-brand-600 transition-colors leading-tight">
                      {cat.category_name}
                    </p>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Mobile — static grid, no horizontal scroll */}
          <div className="grid grid-cols-3 gap-y-5 gap-x-2 md:hidden">
            {Array.isArray(categories) && categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/${cat.slug}`}
                className="flex flex-col items-center gap-2 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={compressImage(cat.cat_img, 300, 70, 'webp')}
                    alt={cat.category_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="text-[11px] font-semibold text-gray-700 leading-tight">
                  {cat.category_name}
                </p>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default PlantersCategory;
